import React, { useEffect, useState } from "react";
import "./../../styles/clients/home.css";
import NewsCard from "../../components/NewsCard";
import { Link } from "react-router-dom";
import PricingCard from "../../components/PricingCard";
import { Check, Handshake } from "lucide-react";
import { PostsService, Post } from "../../services/postsService";
import { toAbsoluteUrl } from "../../config/api";
import { formatDate } from "../../utils/dateFormatter";

const Home: React.FC = () => {
  const getStartedRef = React.useRef<HTMLAnchorElement>(null);
  const learnMoreRef = React.useRef<HTMLAnchorElement>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleHover = () => {
      if (learnMoreRef.current && getStartedRef.current) {
        learnMoreRef.current.classList.add("hovered");
        getStartedRef.current.classList.add("hovered-target");
      }
    };
    const handleMouseLeave = () => {
      if (learnMoreRef.current && getStartedRef.current) {
        learnMoreRef.current.classList.remove("hovered");
        getStartedRef.current.classList.remove("hovered-target");
      }
    };
    const learnMoreElement = learnMoreRef.current;
    if (learnMoreElement) {
      learnMoreElement.addEventListener("mouseenter", handleHover);
      learnMoreElement.addEventListener("mouseleave", handleMouseLeave);
    }
    return () => {
      if (learnMoreElement) {
        learnMoreElement.removeEventListener("mouseenter", handleHover);
        learnMoreElement.removeEventListener("mouseleave", handleMouseLeave);
      }
    };
  }, []);

  // Lấy các bài viết hàng đầu cho trang chủ
  useEffect(() => {
    const fetchTopPosts = async () => {
      try {
        setLoading(true);
        setError(null);

        // Lấy 8 bài viết được sắp xếp theo lượt xem (lượt xem cao nhất trước)
        const response = await PostsService.getPublicPosts({
          page: 1,
          limit: 8,
          sortBy: "views",
        });

        setPosts(response.posts);
      } catch (err) {
        setError("Không thể tải danh sách bài viết. Vui lòng thử lại sau.");
      } finally {
        setLoading(false);
      }
    };

    fetchTopPosts();
  }, []);

  // Hàm trợ giúp để chuyển đổi dữ liệu bài đăng API thành thuộc tính NewsCard
  const transformPostToNewsCard = (post: Post) => {
    // Xử lý featuredImage: ưu tiên lấy từ database, chuyển đổi thành URL đầy đủ
    let imageUrl = "";
    if (post.featuredImage && post.featuredImage.trim()) {
      // Nếu là URL đầy đủ (http/https), giữ nguyên
      // Nếu là đường dẫn tương đối, chuyển đổi thành URL đầy đủ
      imageUrl = post.featuredImage.startsWith('http') 
        ? post.featuredImage 
        : toAbsoluteUrl(post.featuredImage);
    }
    
    return {
      id: post.id,
      slug: post.slug,
      title: post.title,
      date: formatDate(post.createdAt),
      author: post.author?.fullName || "Tác giả",
      authorAvatar: toAbsoluteUrl(post.author?.avatar || ""),
      location: "Việt Nam",
      content: post.content.substring(0, 150) + "...",
      imageUrl: imageUrl, // Chỉ truyền URL nếu có, NewsCard sẽ xử lý fallback
      tag: post.category?.name || "Tin tức",
      views: post.viewCount || 0,
      externalLinks: post.externalLinks || [],
    };
  };

  return (
    <div className="home-container">
      <header className="header-section">
        <div className="header-tag">
          <Handshake size={14} /> Được hơn 10.000+ chuyên gia SEO tin dùng
        </div>
        <div className="header-content">
          <h1>
            Xây dựng backlink chất lượng cao <br />
            <span className="custom-font"> dễ dàng và hiệu quả </span>
          </h1>
          <p>
            Kết nối với hàng ngàn website uy tín, tạo backlink tự nhiên và an
            toàn. Nâng cao thứ hạng
            <br />
            SEO của bạn với chi phí tối ưu và hiệu quả đã được chứng minh.
          </p>
          <div className="header-actions">
            <Link
              to="/posts/create"
              className="get-started-button link-no-underline"
              ref={getStartedRef}
            >
              Đăng tin miễn phí
            </Link>
            <Link
              to="/news"
              className="learn-more-button link-no-underline"
              ref={learnMoreRef}
            >
              Xem bài viết
            </Link>
          </div>
          <div className="header-options">
            <label>
              <Check color="#007bff" size={14} /> Không cần thẻ tín dụng
            </label>
            <label>
              <Check color="#007bff" size={14} /> 2-4 Backlink miễn phí mỗi ngày
            </label>
            <label>
              <Check color="#007bff" size={14} /> Huỷ bất cứ lúc nào
            </label>
          </div>
        </div>
      </header>
      <section className="pricing-section">
        <div className="pricing-title">
          <h2>Chọn gói phù hợp cho bạn</h2>
          <p>
            Giải pháp tối ưu để phát triển kinh doanh và tăng trưởng bền vững.
            <br /> Các gói dịch vụ được thiết kế linh hoạt, phù hợp cho mọi nhu
            cầu.
          </p>
        </div>
        <PricingCard />
      </section>
      <section className="news-section">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Đang tải bài viết...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <p className="error-message">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="retry-button"
            >
              Thử lại
            </button>
          </div>
        ) : (
          <div className="news-grid">
            {posts.length > 0 ? (
              posts.map((post) => (
                <div className="news-grid-item" key={post.id}>
                  <NewsCard {...transformPostToNewsCard(post)} />
                </div>
              ))
            ) : (
              <div className="no-posts-container">
                <p>Chưa có bài viết nào được đăng.</p>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
};

export default Home;
